import { animated, IntersectionArgs, useInView } from '@react-spring/web';
import React, { PropsWithChildren, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { FullScreenSpinner } from '../../components/molecules/Spinner';
import CardSlashIcon from '../../components/Icons/CardSlashIcon';
import CredentialOffer from '../offer/CredentialOffer';
import { useTractionService } from '../../services';
import { IThemeContext, useCredentialData, useThemeContext } from '../../contexts';
import { useNavigate } from 'react-router-dom';
import { CredentialExchangeRecord } from '../../types/credentials';
import { getResponsivePadding, getFluidFontSize, getDeviceInfo } from '../../helpers/utils';

/**
 * @description Credential offer list component
 * @return {React.FC}
 */
const CredentialOfferList: React.FC<PropsWithChildren> = () => {
  const navigate = useNavigate();
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const service = useTractionService();
  const { theme } = useThemeContext();
  const { offers, fetchOffers, loading, removeOffer } = useCredentialData();
  const deviceInfo = getDeviceInfo();

  const styles = createCredentialOfferListStyles(theme);

  const refresh = async () => {
    setLocalLoading(true);
    await fetchOffers();
    setLocalLoading(false);
  };

  const onAccept = async (credentialId: string) => {
    await service.acceptCredentialRecord(credentialId);
  };

  const onDecline = async (credentialId: string, credential: CredentialExchangeRecord) => {
    const offerLength = removeOffer(credential);
    service.removeCredentialRecord(credentialId);
    if (offerLength < 1) {
      navigate('/');
    }
  };

  // Icon Animation
  const [iconAnim, iconAnimStyle] = useInView(
    () => ({
      from: {
        opacity: 0,
        transform: 'translateY(40px)'
      },
      to: {
        opacity: 1,
        transform: 'translateY(0)'
      }
    }),
    [loading] as IntersectionArgs
  );

  // Text Animation
  const [textAnim, textAnimStyle] = useInView(
    () => ({
      from: {
        opacity: 0,
        transform: 'translateY(40px)'
      },
      to: {
        opacity: 1,
        transform: 'translateY(0)',
        delay: 100
      }
    }),
    [loading] as IntersectionArgs
  );

  const [buttonAnim, buttonAnimStyle] = useInView(
    () => ({
      from: {
        opacity: 0,
        transform: 'translateY(40px)'
      },
      to: {
        opacity: 1,
        transform: 'translateY(0)',
        delay: 200
      }
    }),
    [loading] as IntersectionArgs
  );

  // Responsive icon size
  const iconSize = deviceInfo.isMobile ? 100 : deviceInfo.isTablet ? 120 : 140;

  if ((loading && offers.length < 1) || localLoading) {
    return <FullScreenSpinner text={'Checking for offers...'} />;
  }

  return (
    <View style={styles.container}>
      {offers.length === 0 ? (
        <View style={styles.emptyListStyle}>
          <View style={{ flex: 1, justifyContent: 'flex-end' }}>
            <animated.div ref={iconAnim} style={iconAnimStyle}>
              <CardSlashIcon iconWidth={iconSize} iconHeight={iconSize}></CardSlashIcon>
            </animated.div>
          </View>

          <View>
            <animated.div ref={textAnim} style={textAnimStyle}>
              <View style={styles.emptyListTextStyle}>
                <Text style={styles.emptyListMessageTitleStyle}>No offers</Text>
                <Text style={styles.emptyListMessageSubtitleStyle}>
                  New credential offers will appear here.
                </Text>
              </View>
            </animated.div>
          </View>

          <View style={{ flex: 1, justifyContent: 'flex-end', padding: 0, width: '100%' }}>
            <animated.div ref={buttonAnim} style={buttonAnimStyle}>
              <View style={styles.emptyListButtonsContainer}>
                <Pressable
                  style={[styles.buttonStyle, { marginBottom: getResponsivePadding(8, 9, 10) }]}
                  role="button"
                  onPress={async () => {
                    await refresh();
                  }}>
                  <Text style={styles.buttonText}>Refresh</Text>
                </Pressable>
                <Pressable
                  style={[styles.buttonStyle, styles.buttonSecondaryStyle]}
                  role="button"
                  onPress={() => {
                    navigate('/');
                  }}>
                  <Text style={[styles.buttonText, styles.buttonTextSecondary]}>
                    Back to Wallet
                  </Text>
                </Pressable>
              </View>
            </animated.div>
          </View>
        </View>
      ) : (
        <View style={styles.listContainer}>
          {offers.map((item, index) => {
            return (
              <View style={styles.offerItemContainer} key={index}>
                <CredentialOffer
                  credential={item}
                  primaryAction={{
                    title: 'Accept Offer',
                    action: () => onAccept(item.credentialExchangeId)
                  }}
                  secondaryAction={{
                    title: 'Reject Offer',
                    action: () => onDecline(item.credentialExchangeId, item)
                  }}
                />
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
};

export default CredentialOfferList;

const createCredentialOfferListStyles = (
  theme: IThemeContext['theme']
): ReturnType<typeof StyleSheet.create> =>
  StyleSheet.create({
    container: {
      width: '100%',
      height: '100%'
    },
    listContainer: {
      width: '100%'
    },
    offerItemContainer: {
      marginBottom: getResponsivePadding(8, 10, 12)
    },
    emptyListStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: getResponsivePadding(28, 32, 36),
      width: '100%',
      height: '100%',
      paddingHorizontal: getResponsivePadding(16, 20, 24)
    },
    emptyListTextStyle: {
      display: 'flex',
      alignItems: 'center',
      gap: getResponsivePadding(8, 10, 12),
      flex: 1,
      width: '100%'
    },
    emptyListMessageTitleStyle: {
      fontSize: getFluidFontSize(20, 22, 24, 18, 28),
      color: theme.textPrimary,
      fontWeight: '700',
      textAlign: 'center',
      width: '100%'
    },
    emptyListMessageSubtitleStyle: {
      fontSize: getFluidFontSize(14, 16, 18, 12, 20),
      fontWeight: '400',
      color: theme.textSecondary,
      textAlign: 'center',
      width: '100%',
      paddingHorizontal: getResponsivePadding(8, 12, 16)
    },
    buttonStyle: {
      paddingVertical: getResponsivePadding(12, 14, 16),
      paddingHorizontal: getResponsivePadding(20, 22, 24),
      borderRadius: 6,
      backgroundColor: theme.primary,
      color: '#fff',
      textAlign: 'center',
      boxShadow: '1px 2px 4px rgba(41, 45, 50, 0.5)',
      width: '100%'
    },
    buttonText: {
      textAlign: 'center',
      color: 'inherit',
      fontSize: getFluidFontSize(14, 15, 16, 12, 18),
      fontWeight: '600',
      width: '100%'
    },
    buttonSecondaryStyle: {
      backgroundColor: 'white',
      borderColor: 'Black',
      borderWidth: 1
    },
    buttonTextSecondary: {
      color: 'Black'
    },
    emptyListButtonsContainer: {
      width: '100%',
      alignSelf: 'center'
    }
  });
